import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, RefreshCw, LogOut, MessageCircle, AlertTriangle, CheckCircle2, Users, MessageSquare } from 'lucide-react';
import { whatsappApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';

// Every user links their own phone here — an invoice then goes to the customer
// from the number of whoever sent it. Scanning is the same pairing WhatsApp
// uses for a laptop. Administrators also see who else is linked.

type Status = {
  enabled: boolean;
  state: 'starting' | 'qr' | 'connected' | 'disconnected';
  linked: boolean;
  number: string | null;
  qr: string | null;
  error: string | null;
  /** Devices asking for a message they could not decrypt, newest first. */
  retries?: Array<{ at: number; to: string; id: string; answered: boolean }>;
  wa_version?: string | null;
  stored_messages?: number;
};
type Account = { user_id: string; name: string | null; linked: boolean; state: string; number: string | null; last_used_at: string | null };

const WhatsappLink: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<Status | null>(null);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [busy, setBusy] = useState<null | 'connect' | 'logout'>(null);
  // What this account has sent. Kept by the portal, so it is readable even
  // when the phone's own copy of a message is still being decrypted.
  const [sent, setSent] = useState<Array<{ id: number; to_number: string; kind: string; body: string | null; filename: string | null; sent_at: string }>>([]);
  const timer = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      whatsappApi.getOutbox(50)
        .then(r => setSent(r.data || []))
        .catch(() => { /* an older server has no outbox; the page still works */ });
      const res = await whatsappApi.getStatus();
      setStatus((res as any).data);
    } catch (e: any) {
      showError('WhatsApp', e?.message || 'Could not read the gateway status');
    }
  }, [showError]);

  const loadAccounts = useCallback(async () => {
    if (!isAdmin?.()) return;
    try {
      const res = await whatsappApi.getAccounts();
      setAccounts((res as any).data?.accounts || []);
    } catch { /* not fatal — the admin table is extra */ }
  }, [isAdmin]);

  // A QR expires every ~20s and a new one is issued, so poll while one is on
  // screen; otherwise a slow tick is enough to notice a drop.
  useEffect(() => {
    load(); loadAccounts();
    const tick = () => { load(); timer.current = setTimeout(tick, status?.state === 'qr' ? 3000 : 10000); };
    timer.current = setTimeout(tick, status?.state === 'qr' ? 3000 : 10000);
    return () => clearTimeout(timer.current);
  }, [load, loadAccounts, status?.state]);

  const connect = async () => {
    setBusy('connect');
    try {
      const res = await whatsappApi.connect();
      setStatus((res as any).data);
      showSuccess('WhatsApp', 'Starting — scan the QR when it appears.');
    } catch (e: any) {
      showError('WhatsApp', e?.message || 'Could not start your WhatsApp session');
    } finally { setBusy(null); }
  };

  const logout = async () => {
    if (!window.confirm('Unlink your number? You will not be able to send invoices from WhatsApp until you scan again.')) return;
    setBusy('logout');
    try {
      const res = await whatsappApi.logout();
      setStatus((res as any).data);
      loadAccounts();
      showSuccess('WhatsApp', 'Your number is unlinked');
    } catch (e: any) {
      showError('WhatsApp', e?.message || 'Could not unlink');
    } finally { setBusy(null); }
  };

  const unlinkOther = async (userId: string) => {
    const who = accounts?.find(a => a.user_id === userId)?.name || userId;
    if (!window.confirm(`Unlink ${who}'s WhatsApp? They will have to scan again to send invoices.`)) return;
    try {
      await whatsappApi.logoutUser(userId);
      showSuccess('WhatsApp', `${who} unlinked`);
      loadAccounts(); load();
    } catch (e: any) {
      showError('WhatsApp', e?.message || 'Could not unlink that user');
    }
  };

  const badge = () => {
    switch (status?.state) {
      case 'connected':    return <span className="inline-flex items-center gap-1 text-[12px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"><CheckCircle2 size={13} /> Linked</span>;
      case 'qr':           return <span className="inline-flex items-center gap-1 text-[12px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1">Waiting for scan</span>;
      case 'starting':     return <span className="inline-flex items-center gap-1 text-[12px] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">Starting…</span>;
      default:             return status?.linked
        ? <span className="inline-flex items-center gap-1 text-[12px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">Linked · sleeping</span>
        : <span className="inline-flex items-center gap-1 text-[12px] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">Not linked</span>;
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-none flex items-center gap-2 px-3 py-2.5 bg-white border-b border-slate-200">
        <button onClick={() => navigate(-1)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"><ChevronLeft size={18} /></button>
        <MessageCircle size={16} className="text-green-600" />
        <h1 className="text-[15px] font-bold text-slate-800">My WhatsApp</h1>
        <div className="ml-2">{badge()}</div>
        <button onClick={() => { load(); loadAccounts(); }} className="ml-auto p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} /></button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {!status?.enabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-[13px] text-amber-900">
              <div className="font-bold mb-1 flex items-center gap-1.5"><AlertTriangle size={15} /> The gateway is switched off on this server</div>
              An administrator needs to add <code className="bg-white/70 px-1 rounded">WHATSAPP_WEB_ENABLED=true</code> to <code className="bg-white/70 px-1 rounded">backend/.env</code> and restart the backend.
            </div>
          )}

          {status?.state === 'connected' && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-[13px] text-slate-600">Your invoices go out from</div>
              <div className="text-[18px] font-bold text-slate-800 tabular-nums">+{status.number}</div>
              <div className="text-[12px] text-slate-500 mt-1">Keep this phone online — WhatsApp links a device, it does not replace the phone.</div>
              <button onClick={logout} disabled={busy === 'logout'}
                className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-3 py-1.5 disabled:opacity-50">
                <LogOut size={13} /> {busy === 'logout' ? 'Unlinking…' : 'Unlink my number'}
              </button>
            </div>
          )}

          {status?.enabled && status.state !== 'connected' && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
              {status.qr ? (
                <>
                  <img src={status.qr} alt="WhatsApp pairing QR" className="mx-auto w-[280px] h-[280px]" />
                  <div className="text-[13px] text-slate-600 mt-3 text-left max-w-sm mx-auto">
                    On your phone: <strong>WhatsApp → Settings → Linked devices → Link a device</strong>, then scan this code. It refreshes every few seconds.
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[13px] text-slate-600 mb-3">
                    {status.state === 'starting' ? 'Starting…'
                      : status.linked ? 'Your number is linked; the connection is asleep and wakes up on the next send.'
                      : 'Link your phone to send invoices on WhatsApp.'}
                    {status.error && <div className="text-red-600 mt-1">{status.error}</div>}
                  </div>
                  <button onClick={connect} disabled={busy === 'connect'}
                    className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg px-4 py-2 disabled:opacity-50">
                    <MessageCircle size={14} /> {busy === 'connect' ? 'Starting…' : status.linked ? 'Reconnect now' : 'Show pairing QR'}
                  </button>
                  {status.linked && (
                    <button onClick={logout} disabled={busy === 'logout'}
                      className="ml-2 inline-flex items-center gap-1.5 text-[13px] font-bold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-3 py-2 disabled:opacity-50">
                      <LogOut size={13} /> Unlink
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {isAdmin?.() && accounts && accounts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-1.5 text-[13px] font-bold text-slate-700">
                <Users size={14} /> Linked users
              </div>
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">User</th>
                    <th className="text-left px-4 py-2 font-semibold">Number</th>
                    <th className="text-left px-4 py-2 font-semibold">State</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.user_id} className="border-t border-slate-100">
                      <td className="px-4 py-2">
                        <div className="font-semibold text-slate-800">{a.name || a.user_id}</div>
                        {a.name && <div className="text-[11px] text-slate-400">{a.user_id}</div>}
                      </td>
                      <td className="px-4 py-2 tabular-nums text-slate-600">{a.number ? `+${a.number}` : '—'}</td>
                      <td className="px-4 py-2 text-slate-600">{a.state === 'connected' ? 'Connected' : a.linked ? 'Linked · sleeping' : 'Not linked'}</td>
                      <td className="px-4 py-2 text-right">
                        {a.linked && (
                          <button onClick={() => unlinkOther(a.user_id)} className="text-[11px] font-bold text-red-600 hover:underline">Unlink</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Whether the other devices on this account are asking for a message
              again — the one fact that says whether a bubble stuck on "Waiting
              for this message" is waiting on this server or on WhatsApp. */}
          {status?.linked && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-[12.5px]">
              <div className="font-bold text-slate-700 mb-1.5 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-500" />
                Decryption retries
                {typeof status.stored_messages === 'number' && (
                  <span className="ml-auto text-[11px] font-normal text-slate-400">
                    {status.stored_messages} message(s) kept for re-sending
                    {status.wa_version ? ` · WhatsApp ${status.wa_version}` : ''}
                  </span>
                )}
              </div>
              {(status.retries || []).length === 0 ? (
                <div className="text-slate-500">
                  No device has asked for a message again since this server started. A message still
                  showing “Waiting for this message” is not waiting on us — unlink and scan again to
                  rebuild the encryption between your phone and this device.
                </div>
              ) : (
                <div className="space-y-1">
                  {(status.retries || []).map(r => (
                    <div key={r.id + r.at} className="flex items-center gap-2">
                      <span className="font-mono text-slate-700">{r.to}</span>
                      <span className={r.answered ? 'text-emerald-700' : 'text-red-600'}>
                        {r.answered ? 'asked again — sent back' : 'asked again — not in the store'}
                      </span>
                      <span className="ml-auto text-[11px] text-slate-400">
                        {new Date(r.at).toLocaleTimeString('en-GB')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* The phone sometimes cannot decrypt its own copy of what a linked
              device sent and shows "Waiting for this message" instead. This
              record is the portal's own and is always readable. */}
          {sent.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <MessageSquare size={14} className="text-emerald-600" />
                <span className="font-bold text-slate-700 text-[13px]">Sent from the portal</span>
                <span className="text-[11px] text-slate-400">last {sent.length}</span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {sent.map(m => (
                  <div key={m.id} className="px-4 py-2 text-[12.5px]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-700">{m.to_number}</span>
                      {m.kind === 'document' && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold uppercase">
                          {m.filename || 'file'}
                        </span>
                      )}
                      <span className="ml-auto text-[11px] text-slate-400">
                        {new Date(String(m.sent_at).replace(' ', 'T')).toLocaleString('en-GB')}
                      </span>
                    </div>
                    {m.body && <div className="text-slate-600 whitespace-pre-wrap mt-0.5">{m.body}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-[12.5px] text-slate-600 leading-relaxed">
            <div className="font-bold text-slate-700 mb-1">Worth knowing</div>
            This links a normal WhatsApp account, the way WhatsApp Web does. It is not WhatsApp's official business API,
            so keep the volume human: send invoices as you raise them, not in bulk, and never to people who did not ask
            for them. A number that is reported or floods messages can be banned by WhatsApp, and that ban applies to the
            number itself, not just this server.
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsappLink;
